/**
 * `<CapCohortStatePanel>` — section 5 of the B-1 VM Debug pane.
 *
 * Collapsed-by-default disclosure with the current capability /
 * cohort state surfaced from the diagnostics bundle:
 *
 *   - capability prefix (no HMAC body — TDD §C-1 redaction)
 *   - capability expiry
 *   - cohort flag value + allowlist match
 *   - routing decision (`local` / `cloud` / `engineering-flag`)
 *
 * Sources data from C-1's `app:get-diagnostic-bundle` IPC. Rendering
 * is read-only — engineer cannot mutate state from this panel. When
 * the bundle's `vm` field is `null` (flag-OFF), the panel renders a
 * "cloud-VM flag off" placeholder.
 */

import { useEffect, useState } from 'react';

interface CapState {
  prefix: string;
  expiresAt: string;
}

interface CohortFlagState {
  flagValue: string;
  allowlistMatch: boolean;
}

interface VmDiagnosticsSnapshot {
  capState: CapState | null;
  cohortFlagState: CohortFlagState;
  routingDecision: 'local' | 'cloud' | 'engineering-flag';
}

export interface CapCohortStatePanelProps {
  /** Bugbot round-4 LOW fix: seed the `<details>` `open` attribute. The
   *  earlier draft passed this through `key` on the parent, which forced
   *  a remount that reset state to collapsed — dead code that defied the
   *  prop's documented purpose. Now wired as a real default. */
  defaultExpanded?: boolean;
}

export function CapCohortStatePanel({
  defaultExpanded = false,
}: CapCohortStatePanelProps = {}): React.JSX.Element {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [snapshot, setSnapshot] = useState<
    VmDiagnosticsSnapshot | null | 'flag-off' | 'pending'
  >('pending');

  useEffect(() => {
    // Bugbot round-4 LOW fix: fetch only on the FIRST time the panel
    // is opened, not on every toggle. Diagnostics don't change within
    // a workspace session frequently enough to justify re-fetching
    // when the user collapses + re-opens. Once the first fetch
    // resolves, subsequent expand events render the cached snapshot
    // without re-hitting the IPC bridge.
    if (!expanded) return;
    if (snapshot !== 'pending') return;
    if (typeof window === 'undefined' || !window.crucible) {
      setSnapshot(null);
      return;
    }
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        // Focused IPC — surfaces just the `VmDiagnostics` subset, not
        // the full bundle (no log scan or system-tool probe). Producer
        // reuses the same `cloudVmDiagnosticsProvider` closure the
        // diagnostic bundle uses, so the two surfaces never drift.
        const vm = await window.crucible.invoke('app:get-vm-diagnostics');
        if (cancelled) return;
        if (!vm) {
          setSnapshot('flag-off');
          return;
        }
        setSnapshot({
          capState: vm.capState,
          cohortFlagState: vm.cohortFlagState,
          routingDecision: vm.routingDecision,
        });
      } catch {
        // Best-effort — leave the snapshot in the "pending" state so a
        // future collapse + re-expand re-triggers the effect (the
        // `snapshot !== 'pending'` guard at the top of the effect
        // would otherwise lock the panel into a terminal error and
        // defeat the retry the comment claims to preserve).
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [expanded, snapshot]);

  return (
    <details
      data-testid="vm-debug-cap-cohort-state"
      open={expanded}
      onToggle={(e) => setExpanded((e.target as HTMLDetailsElement).open)}
      className="border-t border-white/10 bg-black/30 px-3 py-1.5 text-[10px]"
      style={{ ['--cap-cohort-h' as never]: '32px' }}
    >
      <summary className="cursor-pointer text-white/50 hover:text-white/80">
        Capability + cohort state
      </summary>
      <div className="mt-2 space-y-1 font-mono text-white/70">
        {snapshot === 'pending' ? (
          <div className="text-white/40">Loading…</div>
        ) : snapshot === 'flag-off' ? (
          <div className="text-white/40">
            Cloud-VM flag is OFF — no VM state to surface.
          </div>
        ) : snapshot === null ? (
          <div className="text-white/40">
            Could not load diagnostic bundle.
          </div>
        ) : (
          <>
            <div>
              <span className="text-white/40">cap:</span>{' '}
              {snapshot.capState
                ? `${snapshot.capState.prefix}… (expires ${snapshot.capState.expiresAt})`
                : '(no capability bound)'}
            </div>
            <div>
              <span className="text-white/40">cohort:</span>{' '}
              {snapshot.cohortFlagState.flagValue} (allowlist match:{' '}
              {String(snapshot.cohortFlagState.allowlistMatch)})
            </div>
            <div>
              <span className="text-white/40">routing:</span>{' '}
              {snapshot.routingDecision}
            </div>
          </>
        )}
      </div>
    </details>
  );
}
